import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!this.#checkFormValidity()) return;
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   * Should only be called AFTER the item has been successfully added to the cart.
   */
  animateFlyToCart() {
    // Skip the animation inside a quick-add modal
    if (this.closest('.quick-add-modal')) return;

    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Checks if the form is valid when the user adds an item to cart.
   * Currently only checks the gift card recipient form.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  #checkFormValidity() {
    const form = this.closest('form');
    if (!form) return true;

    const allInputs = Array.from(form.querySelectorAll('input, select, textarea')).filter((input) =>
      input.id.includes('Recipient')
    );
    let allInputsValid = true;
    for (const input of allInputs) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement ||
          input instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      // Skip disabled inputs
      if (input.disabled) continue;

      // Check validity on all input elements
      if (!input.checkValidity()) {
        allInputsValid = false;
        break;
      }
    }
    return allInputsValid;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} checkoutOverlay - The checkout overlay element.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Handle checkout overlay initial state
    const { variantId, addToCartButtonContainer, checkoutOverlay } = this.refs;
    setTimeout(() => {
      if (!this.isAllOptionsSelected()) {
        addToCartButtonContainer?.disable();
        if (checkoutOverlay) {
          checkoutOverlay.style.display = 'block';
        }
      }
    }, 200);

    if (checkoutOverlay) {
      checkoutOverlay.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Validate both Shopify variant dropdowns AND Globo Product Options
        const variantsOk = this.validateVariantDropdowns();
        const globoOk = this.validateGloboOptions();
        if (variantsOk && globoOk) {
          // All valid — hide overlay so next click reaches checkout
          checkoutOverlay.style.display = 'none';
        }
      }, { signal });
    }



    // Monitor Globo Swatches initialization
    let checkGloboCount = 0;
    const checkGloboInterval = setInterval(() => {
      const globoGroups = document.querySelectorAll('.globo-swatch-product-detail, .swatch--gl');
      if (globoGroups.length > 0) {
        this.resetGloboSwatches();
        clearInterval(checkGloboInterval);
      }
      checkGloboCount++;
      if (checkGloboCount > 50) {
        clearInterval(checkGloboInterval);
      }
    }, 100);

    // Monitor clicks on Globo Swatches AND Globo Product Options dropdowns
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const isGloboClick = target.closest(
        '.globo-swatch-product-detail, .swatch--gl, ' +
        '.bo-product-options, [class^="gpf-"], [class*="gpf-"]'
      );
      if (isGloboClick) {
        setTimeout(() => {
          const { addToCartButtonContainer, checkoutOverlay } = this.refs;
          if (this.isAllOptionsSelected()) {
            addToCartButtonContainer?.enable();
            if (checkoutOverlay) checkoutOverlay.style.display = 'none';
            // Clear red borders from globo swatch groups
            const globoGroups = document.querySelectorAll('.globo-swatch-product-detail, .swatch--gl');
            globoGroups.forEach(group => {
              const dropdownBtn = group.querySelector('.dropdown-button-gsw, .dropdown-button-gsw-productgroup') || group;
              if (dropdownBtn instanceof HTMLElement) {
                dropdownBtn.style.borderColor = '';
                dropdownBtn.style.boxShadow = '';
              }
            });
          } else {
            addToCartButtonContainer?.disable();
            if (checkoutOverlay) checkoutOverlay.style.display = 'block';
          }
        }, 80);
      }
    }, { signal });

    // Also watch native change events on Globo-injected hidden selects / inputs
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const inGlobo = target.closest(
        '.bo-product-options, [class^="gpf-"], [class*="gpf-"]'
      );
      if (!inGlobo) return;
      setTimeout(() => {
        const { addToCartButtonContainer, checkoutOverlay } = this.refs;
        if (this.isAllOptionsSelected()) {
          addToCartButtonContainer?.enable();
          if (checkoutOverlay) checkoutOverlay.style.display = 'none';
        } else {
          addToCartButtonContainer?.disable();
          if (checkoutOverlay) checkoutOverlay.style.display = 'block';
        }
      }, 80);
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (!this.validateVariantDropdowns()) {
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Item saved successfully → now switch the button state (checkmark / "Added")
          this.refs.addToCartButtonContainer?.animateAddToCart();

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // Item saved and sent successfully → now run the fly-to-cart animation
          this.refs.addToCartButtonContainer?.animateFlyToCart();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { variantId, addToCartButtonContainer, checkoutOverlay } = this.refs;

    if (!event || !event.detail || event.detail.resource == null) {
      if (variantId) variantId.value = '';
      addToCartButtonContainer?.disable();
      if (checkoutOverlay) {
        checkoutOverlay.style.display = 'block';
      }
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      if (checkoutOverlay) checkoutOverlay.style.display = 'block';
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      if (checkoutOverlay) checkoutOverlay.style.display = 'none';
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  isAllOptionsSelected() {
    let isValid = true;
    const section = this.closest('.shopify-section');
    if (!section) return true;

    // Check Shopify native variant selects
    const selects = section.querySelectorAll('.variant-option__select');
    selects.forEach(el => {
      if (el instanceof HTMLSelectElement && el.value === "") {
        isValid = false;
      }
    });

    // Check Globo Swatches (image/color swatches rendered by Globo Swatches app)
    const globoGroups = document.querySelectorAll('.globo-swatch-product-detail, .swatch--gl');
    globoGroups.forEach(group => {
      const checkedInput = group.querySelector('input:checked, .is-gsw-active');
      const dropdownBtn = group.querySelector('.dropdown-button-gsw, .dropdown-button-gsw-productgroup');

      let isGroupSelected = !!checkedInput;
      if (dropdownBtn && dropdownBtn.textContent && dropdownBtn.textContent.trim().toLowerCase().startsWith('select')) {
        isGroupSelected = false;
      }

      if (!isGroupSelected) {
        isValid = false;
      }
    });

    // Check Globo Product Options hidden selects (Image Drop-Down Menu, etc.)
    // Real class names: .bo-product-options container, .gpf-* field elements
    const scope = section.closest('.shopify-section') || document.documentElement;
    const globoSelects = scope.querySelectorAll(
      '.bo-product-options select, [class*="gpf-"] select, ' +
      '.globo-option-container select, .globo-product-option select'
    );
    globoSelects.forEach(el => {
      if (el instanceof HTMLSelectElement) {
        const v = el.value;
        if (!v || v === '' || v.toLowerCase().startsWith('select') || v.toLowerCase().startsWith('choose')) {
          isValid = false;
        }
      }
    });

    // Check Globo Product Options radio groups
    const globoRadioGroups = new Map();
    scope.querySelectorAll(
      '.bo-product-options input[type="radio"], [class*="gpf-"] input[type="radio"]'
    ).forEach(input => {
      if (input instanceof HTMLInputElement && input.name) {
        if (!globoRadioGroups.has(input.name)) globoRadioGroups.set(input.name, false);
        if (input.checked) globoRadioGroups.set(input.name, true);
      }
    });
    globoRadioGroups.forEach(checked => { if (!checked) isValid = false; });

    return isValid;
  }

  /**
   * Validates Globo Product Options fields and shows inline red errors.
   * Called when the checkout overlay is clicked.
   * @returns {boolean}
   */
  validateGloboOptions() {
    const section = /** @type {Element} */ (this.closest('.shopify-section')) || document.documentElement;
    let isValid = true;
    /** @type {HTMLElement | null} */
    let firstInvalid = null;

    // ── Hidden selects injected by Globo Product Options ──────────────────────
    // Real class names: .bo-product-options (container), [class*="gpf-"] (fields)
    const globoSelects = section.querySelectorAll(
      '.bo-product-options select, [class*="gpf-"] select'
    );

    globoSelects.forEach(el => {
      if (!(el instanceof HTMLSelectElement)) return;
      const v = el.value;
      const isEmpty = !v || v === '' || v.toLowerCase().startsWith('select') || v.toLowerCase().startsWith('choose');
      const container = /** @type {HTMLElement | null} */ (
        el.closest('.bo-product-options, [class*="gpf-"]') || el.parentElement
      );
      if (isEmpty) {
        isValid = false;
        if (container) {
          if (!firstInvalid) firstInvalid = container;
          this.#showGloboError(container, 'Please select an option before continuing.');
        }
      } else {
        if (container) this.#clearGloboError(container);
      }
    });

    // ── Radio groups injected by Globo Product Options ─────────────────────────
    const namedGroups = new Map();
    section.querySelectorAll(
      '.bo-product-options input[type="radio"], [class*="gpf-"] input[type="radio"]'
    ).forEach(input => {
      if (!(input instanceof HTMLInputElement) || !input.name) return;
      if (!namedGroups.has(input.name)) namedGroups.set(input.name, { checked: false, container: input.closest('.bo-product-options, [class*="gpf-"]') });
      if (input.checked) namedGroups.get(input.name).checked = true;
    });
    namedGroups.forEach(({ checked, container }) => {
      if (!checked && container instanceof HTMLElement) {
        isValid = false;
        if (!firstInvalid) firstInvalid = container;
        this.#showGloboError(container, 'Please select an option before continuing.');
      } else if (container instanceof HTMLElement) {
        this.#clearGloboError(container);
      }
    });

    // ── Globo Swatches (dropdown style) ───────────────────────────────────────
    const globoGroups = document.querySelectorAll('.globo-swatch-product-detail, .swatch--gl');
    globoGroups.forEach(group => {
      const checkedInput = group.querySelector('input:checked, .is-gsw-active');
      const dropdownBtn = /** @type {HTMLElement | null} */ (
        group.querySelector('.dropdown-button-gsw, .dropdown-button-gsw-productgroup')
      );
      let isGroupSelected = !!checkedInput;
      if (dropdownBtn && dropdownBtn.textContent && dropdownBtn.textContent.trim().toLowerCase().startsWith('select')) {
        isGroupSelected = false;
      }
      if (!isGroupSelected) {
        isValid = false;
        if (dropdownBtn) {
          dropdownBtn.style.borderColor = '#cc0000';
          dropdownBtn.style.boxShadow = '0 0 0 2px #cc0000';
        }
        if (!firstInvalid && dropdownBtn) firstInvalid = dropdownBtn;
        const container = /** @type {HTMLElement | null} */ (group instanceof HTMLElement ? group : null);
        if (container) this.#showGloboError(container, 'Please select an option before continuing.');
      } else {
        if (dropdownBtn) { dropdownBtn.style.borderColor = ''; dropdownBtn.style.boxShadow = ''; }
        const container = /** @type {HTMLElement | null} */ (group instanceof HTMLElement ? group : null);
        if (container) this.#clearGloboError(container);
      }
    });

    if (!isValid && firstInvalid) {
      const el = /** @type {any} */ (firstInvalid);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return isValid;
  }

  /**
   * Shows a red inline error message below a Globo field container.
   * @param {HTMLElement} container
   * @param {string} message
   */
  #showGloboError(container, message) {
    let err = /** @type {HTMLElement | null} */ (container.querySelector('.globo-required-error'));
    if (!err) {
      const sibling = container.nextElementSibling;
      if (sibling && sibling.classList.contains('globo-required-error')) {
        err = /** @type {HTMLElement} */ (sibling);
      }
    }
    if (!err) {
      err = document.createElement('p');
      err.className = 'globo-required-error';
      err.setAttribute('role', 'alert');
      err.style.cssText = 'color:#cc0000;font-size:13px;font-weight:500;margin:6px 0 0;padding:0;line-height:1.4;';
      if (container.parentNode) container.parentNode.insertBefore(err, container.nextSibling);
      const cleanup = () => err && err.remove();
      ['change', 'click', 'input'].forEach(evt =>
        container.addEventListener(evt, cleanup, { once: true, capture: true })
      );
    }
    err.textContent = message;
  }

  /**
   * Removes the inline error for a Globo field container.
   * @param {HTMLElement} container
   */
  #clearGloboError(container) {
    const err = container.querySelector('.globo-required-error') || container.nextElementSibling;
    if (err && err.classList && err.classList.contains('globo-required-error')) err.remove();
  }

  validateVariantDropdowns() {
    const { checkoutOverlay, addToCartTextError } = this.refs;
    const section = this.closest('.shopify-section');
    if (!section) return true;
    const selects = section.querySelectorAll('.variant-option__select');
    let isValid = true;
    /** @type {HTMLElement | null} */
    let firstInvalidSelect = null;

    selects.forEach(el => {
      if (!(el instanceof HTMLSelectElement)) return;
      const select = el;
      const wrapper = select.closest('.variant-option__select-wrapper');
      if (select.value === "") {
        isValid = false;
        if (wrapper) {
          wrapper.classList.add('invalid-select');
        }
        select.setCustomValidity("Please select an option");
        if (!firstInvalidSelect) {
          firstInvalidSelect = select;
        }
        if (!select.dataset.hasValidationListener) {
          select.addEventListener('change', () => {
            if (select.value !== "") {
              wrapper?.classList.remove('invalid-select');
              select.setCustomValidity("");
            }
          });
          select.dataset.hasValidationListener = 'true';
        }
      } else {
        wrapper?.classList.remove('invalid-select');
        select.setCustomValidity("");
      }
    });

    // Validate Globo Swatches options if present
    const globoGroups = document.querySelectorAll('.globo-swatch-product-detail, .swatch--gl');
    globoGroups.forEach(group => {
      const checkedInput = group.querySelector('input:checked, .is-gsw-active');
      const dropdownBtn = group.querySelector('.dropdown-button-gsw, .dropdown-button-gsw-productgroup');

      let isGroupSelected = !!checkedInput;
      if (dropdownBtn && dropdownBtn.textContent && dropdownBtn.textContent.includes('Select')) {
        isGroupSelected = false;
      }

      if (!isGroupSelected) {
        isValid = false;
        const borderTarget = dropdownBtn || group;
        if (borderTarget instanceof HTMLElement) {
          borderTarget.style.borderColor = 'red';
          borderTarget.style.boxShadow = '0 0 0 1px red';
        }

        if (!firstInvalidSelect && (borderTarget instanceof HTMLElement)) {
          firstInvalidSelect = borderTarget;
        }
      } else {
        const borderTarget = dropdownBtn || group;
        if (borderTarget instanceof HTMLElement) {
          borderTarget.style.borderColor = '';
          borderTarget.style.boxShadow = '';
        }
      }
    });

    if (checkoutOverlay) {
      checkoutOverlay.style.display = isValid ? 'none' : 'block';
    }

    if (!isValid && firstInvalidSelect) {
      const el = /** @type {any} */ (firstInvalidSelect);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const errorMessage = "Please select an option first.";
      if (el instanceof HTMLSelectElement) {
        el.focus();
        el.reportValidity();
      }

      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          addToCartTextError.appendChild(document.createTextNode(errorMessage));
        }

        if (this.#timeout) clearTimeout(this.#timeout);
        this.#timeout = setTimeout(() => {
          if (addToCartTextError) addToCartTextError.classList.add('hidden');
        }, 5000);
      }
    }

    return isValid;
  }


  resetGloboSwatches() {
    const url = new URL(window.location.href);
    if (url.searchParams.has('variant')) return; // Don't reset if variant is in URL

    const globoGroups = document.querySelectorAll('.globo-swatch-product-detail, .swatch--gl');
    if (globoGroups.length === 0) return;

    globoGroups.forEach(group => {
      // Uncheck all radio inputs
      const checkedInputs = group.querySelectorAll('input:checked');
      checkedInputs.forEach(input => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Remove active classes
      group.querySelectorAll('.is-gsw-active').forEach(el => el.classList.remove('is-gsw-active'));
      group.querySelectorAll('.globo-style--button, .globo-border-color-swatch').forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.backgroundColor = '';
          el.style.color = '';
          el.style.borderColor = '';
        }
      });

      // Reset dropdown button text to placeholder
      const dropdownBtn = group.querySelector('.dropdown-button-gsw, .dropdown-button-gsw-productgroup');
      if (dropdownBtn instanceof HTMLElement) {
        const optionLabel = group.closest('.variant-option')?.querySelector('label')?.textContent || 'Option';
        dropdownBtn.textContent = `Select ${optionLabel.trim()}`;
        const caret = document.createElement('span');
        caret.className = 'globo-arrow-select';
        dropdownBtn.appendChild(caret);
      }
    });

    const { variantId, addToCartButtonContainer, checkoutOverlay } = this.refs;
    if (variantId) variantId.value = '';
    addToCartButtonContainer?.disable();
    if (checkoutOverlay) {
      checkoutOverlay.style.display = 'block';
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

// ─── Globo Product Options — Dynamic Checkout Hider ──────────────────────────
// Uses a MutationObserver to scan the DOM for required Globo options.
// Adds `has-required-globo-options` to hide Shopify dynamic checkout buttons.
(function () {
  'use strict';

  function checkRequiredGloboOptions() {
    // Selectors covering any input, select, textarea, or wrapper marked required by Globo
    const requiredSelector = [
      '.bo-product-options [required]',
      '.bo-product-options [data-required="true"]',
      '.bo-product-options .globo-required',
      '.bo-product-options .gpo-required',
      '[class*="gpf-"] [required]',
      '[class*="gpf-"] [data-required="true"]',
      '[class*="gpf-"].globo-required',
      '.globo-option-container [required]',
      '.globo-option-container [data-required="true"]',
      '.globo-option-container .globo-required',
      '[class*="globo"] [required]',
      '[class*="globo"] [data-required="true"]',
      '[class*="globo"].globo-required',
      '[class*="gpo"] [required]',
      '[class*="gpo"] [data-required="true"]',
      '[class*="gpo"].gpo-required',
      '.globo-selector-container [required]',
      '.globo-swatch-product-detail [required]',
      '.swatch--gl [required]'
    ].join(', ');

    const hasRequired = !!document.querySelector(requiredSelector);
    const forms = document.querySelectorAll('product-form-component, form[action*="/cart/add"]');

    if (hasRequired) {
      document.body.classList.add('has-required-globo-options');
      forms.forEach(form => form.classList.add('has-required-globo-options'));
    } else {
      document.body.classList.remove('has-required-globo-options');
      forms.forEach(form => form.classList.remove('has-required-globo-options'));
    }
  }

  // Run immediately and on common load states
  checkRequiredGloboOptions();
  document.addEventListener('DOMContentLoaded', checkRequiredGloboOptions);
  window.addEventListener('load', checkRequiredGloboOptions);

  // Observe subtree mutations as Globo renders fields after page load
  const observer = new MutationObserver(() => {
    checkRequiredGloboOptions();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();